import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { GpsReading } from '../../../domain/telemetry/raw/GpsReading'
import type { Finding } from '../../../telemetryChecks/finding'
import {
  checkGpsReadings,
  gpsFixOf,
} from '../../../telemetryChecks/gpsVerdicts'
import { rejectImplausibleGpsFixes } from '../../../telemetryChecks/readingChecks/implausibleFix'
import { UnusableTelemetryError } from '../../../telemetryChecks/unusableTelemetryError'

const forgetDistanceCountingDroppedFixes = ({
  distanceMeters: _distanceCountingDroppedFixes,
  ...reading
}: GpsReading): GpsReading => reading

const droppedFinding = (dropped: number, of: number): Finding => ({
  check: 'implausibleFix',
  level: 'reading',
  aspect: 'gpsPosition',
  verdict: 'dropped',
  affected: dropped,
  of,
  reason: `Dropped ${dropped} of ${of} GPS fixes with an implausible position`,
})

const tooFewFixesFinding = (kept: number, of: number): Finding => ({
  check: 'implausibleFix',
  level: 'reading',
  aspect: 'gpsPosition',
  verdict: 'unusable',
  reason:
    of < 2
      ? 'The session has only one GPS fix'
      : `Only ${kept} of ${of} GPS fixes are plausible`,
})

export class TelemetryChecksStage implements PipelineStage {
  name = 'Telemetry Checks'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const rawTelemetry = context.rawTelemetry
    if (!rawTelemetry) {
      return { success: false, error: 'No raw telemetry to check' }
    }

    const { trust, findings, verdicts } = checkGpsReadings(rawTelemetry.gps)
    if (!trust.positionsUsable) throw new UnusableTelemetryError(findings)

    const kept = rejectImplausibleGpsFixes(rawTelemetry.gps, gpsFixOf, trust)
    const dropped = rawTelemetry.gps.length - kept.length
    if (dropped > 0) {
      findings.push(droppedFinding(dropped, rawTelemetry.gps.length))
    }
    if (kept.length < 2) {
      throw new UnusableTelemetryError([
        ...findings,
        tooFewFixesFinding(kept.length, rawTelemetry.gps.length),
      ])
    }

    const gps =
      dropped > 0 ? kept.map(forgetDistanceCountingDroppedFixes) : kept

    context.findings.push(...findings)
    context.gpsVerdicts = verdicts
    if (gps !== rawTelemetry.gps)
      context.rawTelemetry = { ...rawTelemetry, gps }
    return {
      success: true,
      warnings:
        findings.length > 0
          ? findings.map((finding) => finding.reason)
          : undefined,
    }
  }
}
